import { type ReactElement, forwardRef } from 'react';

import {
  Typography,
  Label,
  Icon,
  Breadcrumbs,
} from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont/icons';

import clsx from 'clsx';

import { HeaderSkeleton } from '../HeaderSkeleton';
import { HelpButtonOrLink } from '../HelpButtonOrLink';
import { ModifiedIconButton } from '../ModifiedIconButton';
import { WithCounter } from '../WithCounter';
import { AdaptiveButton } from '../AdaptiveButton';

import styles from './styles.module.scss';
import type { HeaderProps } from './types';

export const Header = forwardRef<HTMLElement, HeaderProps>(
  (
    {
      className,
      buttonProps,
      help,
      labelProps,
      title,
      isLoading = false,
      onBackButtonClick,
      backButtonClassName,
      breadcrumbsProps,
      titleVariant = 'h4',
      isDialogHeader = false,
      pinLabel = false,
    },
    ref,
  ): ReactElement => {
    return (
      <header className={clsx(styles.header, className)} ref={ref}>
        {isLoading ? (
          <HeaderSkeleton
            withBreadCums={!!breadcrumbsProps}
            withButton={!!onBackButtonClick}
            isDialogHeader={!!isDialogHeader}
          />
        ) : (
          <>
            {breadcrumbsProps && (
              <Breadcrumbs
                {...breadcrumbsProps}
                className={styles.breadCrumbs}
              />
            )}
            <div className={styles.row}>
              <div className={styles.col}>
                {onBackButtonClick && (
                  <ModifiedIconButton
                    iconName={Icons.ArrowLeft}
                    className={clsx(styles.backButton, backButtonClassName)}
                    onClick={onBackButtonClick}
                  />
                )}
                <div
                  className={clsx(
                    styles.col,
                    styles.inner,
                    pinLabel && styles.pinLabel,
                  )}
                >
                  <Typography
                    variant={titleVariant}
                    className={clsx(styles.title, pinLabel && styles.fullWidth)}
                  >
                    {title}
                  </Typography>
                  {labelProps && (
                    <div
                      className={clsx(styles.labelContainer, styles.pinLabel)}
                    >
                      <Label {...labelProps} />
                    </div>
                  )}
                </div>
              </div>
              {(help || buttonProps) && (
                <div className={styles.rightCol}>
                  {help && (
                    <HelpButtonOrLink {...help}>
                      <Icon size='medium' iconName={Icons.QuestionCircled} />
                    </HelpButtonOrLink>
                  )}
                  {buttonProps && (
                    <WithCounter count={buttonProps.count}>
                      <AdaptiveButton {...buttonProps} />
                    </WithCounter>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </header>
    );
  },
);

Header.displayName = 'Header';
